import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { CustomerStates, SubscriptionStatuses } from '@subscriptions/domain/types/SubscriptionsConst';
import { getBlockChurnDate } from '@subscriptions/presentation/utils';
import { BlockChargeCard } from './BlockChargeCard';
import { BlockChurnCard } from './BlockChurnCard';
import { BlockInvoiceDataCard } from './BlockInvoiceDataCard';
import { BlockLastPaymentStatusCard } from './BlockLastPaymentStatusCard';
import { BlockPaymentMethodCard } from './BlockPaymentMethodCard';

interface BlockSummaryContentProps {
  block: SubscriptionBlockViewDto;
}

/**
 * BlockSummaryContent - Orchestrator for the Summary tab
 *
 * Layout: two columns — the charge card on the left, payment method and fiscal data on the right.
 * Stacked, the right column comes first. A churned block shows the churn card in place of the
 * payment method card, having no active method to manage.
 *
 * The edit and ungroup actions sit on the tab bar, not here.
 *
 * @param {Object} props - Component properties
 * @param {SubscriptionBlockViewDto} props.block - Subscription block data
 * @returns {React.ReactElement} Summary content layout
 *
 * @example
 * <BlockSummaryContent block={subscriptionBlock} />
 */
export const BlockSummaryContent = ({ block }: BlockSummaryContentProps) => {
  const isLicenseAvailable = block.status === SubscriptionStatuses.AVAILABLE;
  const isInactive = block.customerState === CustomerStates.INACTIVE;

  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
      <div className="order-2 min-w-0 lg:order-1">
        <BlockChargeCard block={block} />
      </div>

      <div className="order-1 flex min-w-0 flex-col gap-3 lg:order-2 lg:sticky lg:top-4">
        {block.lastError && <BlockLastPaymentStatusCard lastError={block.lastError} />}

        {isInactive ? (
          <BlockChurnCard blockUuid={block.uuid} churnDate={getBlockChurnDate(block)} />
        ) : (
          <BlockPaymentMethodCard
            blockUuid={block.uuid}
            isLicenseAvailable={isLicenseAvailable}
            paymentMethod={block.paymentMethod}
            totalAmount={block.total}
          />
        )}

        <BlockInvoiceDataCard block={block} />
      </div>
    </div>
  );
};
